import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './AppIcon';
import { supabase } from '../lib/supabase';

const EmployeeInfoModal = ({ isOpen, onClose, data, onUpdateSuccess }) => {
  const { t, i18n } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // State za formu - uključuje i 'cf' za modifikaciju
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    phone: '',
    cf: ''
  });

  // Sinkronizacija podataka
  useEffect(() => {
    if (data) {
      setFormData({
        nome: data.nome || '',
        cognome: data.cognome || '',
        email: data.email || '',
        phone: data.phone || '',
        cf: data.cf || ''
      });
    }
  }, [data, isOpen]);

  if (!isOpen || !data) return null;

  const handleSave = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profili')
        .update({
          nome: formData.nome,
          cognome: formData.cognome,
          email: formData.email,
          phone: formData.phone,
          cf: formData.cf // Ostavlja originalni unos (bez prisilnog uppercase)
        })
        .eq('id', data.id);

      if (error) throw error;

      alert(t('employee_modal.alert_success'));
      setIsEditing(false);
      if (onUpdateSuccess) onUpdateSuccess(); 
    } catch (err) {
      alert(t('employee_modal.alert_error') + ": " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Polja koja se mogu uređivati (uključujući CF)
  const editableFields = [
    { id: 'nome', label: t('employee_modal.first_name'), icon: 'User' },
    { id: 'cognome', label: t('employee_modal.last_name'), icon: 'User' },
    { id: 'cf', label: t('employee_modal.tax_code'), icon: 'Fingerprint' },
    { id: 'email', label: t('employee_modal.email'), icon: 'Mail', type: 'email' },
    { id: 'phone', label: t('employee_modal.phone'), icon: 'Phone' },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card w-full max-w-md rounded-3xl border border-border shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Modal Header */}
        <div className="p-6 border-b border-border flex justify-between items-center bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg text-primary">
              <Icon name={isEditing ? "Edit3" : "Info"} size={20} />
            </div>
            <h3 className="font-bold text-lg text-foreground">
              {isEditing ? t('employee_modal.title_edit') : t('employee_modal.title_info')}
            </h3>
          </div>
          <button 
            onClick={() => { setIsEditing(false); onClose(); }} 
            className="p-2 hover:bg-muted rounded-full transition-colors text-muted-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="flex justify-center">
            <span className="px-3 py-1 bg-muted border border-border rounded-full text-[10px] font-mono text-muted-foreground tracking-tighter">
              ID: {data.id}
            </span>
          </div>

          <div className="space-y-4">
            {editableFields.map((input) => (
              <div key={input.id} className="flex flex-col space-y-1 border-b border-border/50 pb-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name={input.icon} size={12} className="opacity-70" />
                  <span className="text-[10px] font-black uppercase tracking-widest">{input.label}</span>
                </div>
                {isEditing ? (
                  <input
                    type={input.type || 'text'}
                    className="w-full bg-background border border-primary/30 rounded-lg px-3 py-1.5 text-sm font-semibold text-foreground outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                    value={formData[input.id]}
                    onChange={(e) => setFormData({ ...formData, [input.id]: e.target.value })}
                  />
                ) : (
                  <span className="text-sm font-semibold text-foreground ml-5">
                    {data[input.id] || t('common.nd')}
                  </span>
                )}
              </div>
            ))}

            {!isEditing && (
              <>
                <div className="flex flex-col space-y-1 border-b border-border/50 pb-2">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="Calendar" size={12} className="opacity-70" />
                    <span className="text-[10px] font-black uppercase tracking-widest">{t('employee_modal.created_at')}</span>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground ml-5">
                    {data.creato_il ? new Date(data.creato_il).toLocaleDateString(i18n.language === 'bs' ? 'bs-BA' : 'it-IT') : t('common.nd')}
                  </span>
                </div>
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="LogIn" size={12} className="opacity-70" />
                    <span className="text-[10px] font-black uppercase tracking-widest">{t('employee_modal.last_login')}</span>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground ml-5">
                    {data.ultimo_accesso ? new Date(data.ultimo_accesso).toLocaleString(i18n.language === 'bs' ? 'bs-BA' : 'it-IT') : t('employee_modal.never')}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-muted/10 border-t border-border flex gap-3">
          {!isEditing ? (
            <>
              <button 
                onClick={() => setIsEditing(true)}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-md shadow-primary/20"
              >
                <Icon name="Edit2" size={16} /> {t('common.edit')}
              </button>
              <button 
                onClick={onClose}
                className="flex-1 px-6 py-2.5 bg-muted text-foreground rounded-xl font-bold text-sm hover:bg-muted/80 transition-all border border-border"
              >
                {t('common.close')}
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={handleSave}
                disabled={loading}
                className="flex-1 px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="Check" size={16} />}
                {t('common.save')}
              </button>
              <button 
                onClick={() => setIsEditing(false)}
                className="flex-1 px-6 py-2.5 bg-muted text-foreground rounded-xl font-bold text-sm hover:bg-muted/80 transition-all border border-border"
              >
                {t('common.cancel')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeInfoModal;